/**
 * Emoji Reactions Component
 *
 * Allows users to add emoji reactions to comments and see who reacted.
 * Features:
 * - Quick emoji reactions on hover
 * - Show reaction counts
 * - Tooltip showing who reacted
 * - Add custom emoji via picker
 * - Compact design positioned in message header
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Smile, Plus } from 'lucide-react';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import type { EmojiClickData } from 'emoji-picker-react';
import { Z_INDEX } from '@/lib/z-index';

// Common quick reactions
const QUICK_REACTIONS = ['👍', '❤️', '😊', '🎉', '🔥', '👀'];

export interface Reaction {
  emoji: string;
  users: string[]; // Array of user names who reacted
  count: number;
}

interface EmojiReactionsProps {
  messageId: string;
  reactions?: Reaction[];
  currentUserName?: string;
  onAddReaction: (messageId: string, emoji: string) => void;
  onRemoveReaction: (messageId: string, emoji: string) => void;
  className?: string;
}

export const EmojiReactions: React.FC<EmojiReactionsProps> = ({
  messageId,
  reactions = [],
  currentUserName = 'You',
  onAddReaction,
  onRemoveReaction,
  className
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [showQuickReactions, setShowQuickReactions] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const pickerRef = useRef<HTMLDivElement>(null);
  const quickReactionsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Handle clicking on an existing reaction
  const handleReactionClick = (reaction: Reaction) => {
    const userHasReacted = reaction.users.includes(currentUserName);

    if (userHasReacted) {
      onRemoveReaction(messageId, reaction.emoji);
    } else {
      onAddReaction(messageId, reaction.emoji);
    }
  };

  // Handle emoji selection from picker
  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    onAddReaction(messageId, emojiData.emoji);
    setShowPicker(false);
  };

  // Handle quick reaction selection
  const handleQuickReaction = (emoji: string) => {
    onAddReaction(messageId, emoji);
    setShowQuickReactions(false);
  };

  // Close pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }

      if (
        quickReactionsRef.current &&
        !quickReactionsRef.current.contains(event.target as Node)
      ) {
        setShowQuickReactions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get tooltip text for reaction
  const getReactionTooltip = (reaction: Reaction): string => {
    if (reaction.count === 0) return '';
    if (reaction.count === 1) return reaction.users[0];
    if (reaction.count === 2) return `${reaction.users[0]} and ${reaction.users[1]}`;

    const others = reaction.count - 2;
    return `${reaction.users[0]}, ${reaction.users[1]} and ${others} other${others > 1 ? 's' : ''}`;
  };

  return (
    <div className={cn('relative inline-flex items-center', className)}>
      {/* Existing Reactions - Compact Display */}
      {reactions.length > 0 && (
        <div className="flex items-center gap-1 mr-1">
          {reactions.filter(r => r.count > 0).map((reaction, index) => {
            const userHasReacted = reaction.users.includes(currentUserName);

            return (
              <button
                key={`${reaction.emoji}-${index}`}
                onClick={() => handleReactionClick(reaction)}
                className={cn(
                  'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs transition-all',
                  userHasReacted
                    ? 'bg-purple-100 border border-purple-300 hover:bg-purple-200'
                    : 'bg-gray-100 border border-gray-200 hover:bg-gray-200'
                )}
                title={getReactionTooltip(reaction)}
              >
                <span className="text-sm">{reaction.emoji}</span>
                <span className={cn(
                  'text-xs font-medium ml-0.5',
                  userHasReacted ? 'text-purple-700' : 'text-gray-600'
                )}>
                  {reaction.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Add Reaction Button - Smile Icon */}
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => {
            if (buttonRef.current) {
              const rect = buttonRef.current.getBoundingClientRect();
              setPickerPosition({
                top: rect.top,
                left: rect.right - 8 // Align to right with small offset
              });
            }
            setShowQuickReactions(!showQuickReactions);
          }}
          className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
          title="Add reaction"
        >
          <Smile className="h-4 w-4" />
        </button>

        {/* Quick Reactions Popup - Rendered via Portal */}
        {showQuickReactions && createPortal(
          <div
            ref={quickReactionsRef}
            className="fixed bg-white border border-gray-200 rounded-lg shadow-xl p-1.5 flex items-center gap-0.5"
            style={{
              top: `${pickerPosition.top - 40}px`, // Position above button
              left: `${pickerPosition.left - 250}px`, // Align to right
              zIndex: Z_INDEX.MODAL_PICKERS
            }}
          >
            {QUICK_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleQuickReaction(emoji)}
                className="p-1.5 hover:bg-gray-100 rounded text-lg transition-colors"
                title={`React with ${emoji}`}
              >
                {emoji}
              </button>
            ))}

            {/* More emojis button */}
            <button
              onClick={() => {
                setShowQuickReactions(false);
                setShowPicker(true);
              }}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="More emojis"
            >
              <Plus className="h-3.5 w-3.5 text-gray-500" />
            </button>
          </div>,
          document.body
        )}

        {/* Full Emoji Picker - Rendered via Portal */}
        {showPicker && createPortal(
          <div
            ref={pickerRef}
            className="fixed"
            style={{
              top: `${pickerPosition.top - 410}px`, // Position above button (picker height + spacing)
              left: `${pickerPosition.left - 320}px`, // Align to right
              zIndex: Z_INDEX.MODAL_PICKERS
            }}
          >
            <EmojiPicker
              onEmojiClick={handleEmojiSelect}
              theme={Theme.LIGHT}
              width={320}
              height={400}
              searchPlaceHolder="Search emoji..."
              previewConfig={{
                showPreview: false
              }}
            />
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

